import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["className", "style", "id", "arrow", "anchorId", "open", "defaultOpen", "position", "keepMounted", "offset", "focused", "unfocused", "children", "renderAnchor", "anchorEl", "popperOptions", "onClickOutside", "onEscape", "onKeyDown", "onToggle"];
import { detectOverflow } from '@popperjs/core';
import { sizeSpacing01, sizeSpacing04 } from '@resultadosdigitais/tangram-design-tokens';
import PropTypes from 'prop-types';
import React, { forwardRef, useCallback, useEffect, useMemo, useState } from 'react';
import { usePopper } from 'react-popper';
import styled, { css } from 'styled-components';
import { ScopedCssBaseline, ScrollbarStyle } from '../StyleBase';
import { positions, targetWithin } from '../helper';
import { useControlled, useDeprecated, useEventListener, useId, useKeyboardNavigation, useMergeRefs, usePreventScroll } from '../hooks';
import Boundary from '../Boundary';
import { DefaultRegion } from '../regions';
import DropdownInnerProvider from './DropdownInnerProvider';
import DropdownProvider from './DropdownProvider';
import useDropdownContext from './useDropdownContext';
import DropdownAnchor from './DropdownAnchor';
import DropdownCheckbox from './DropdownCheckbox';
import DropdownContainer from './DropdownContainer';
import DropdownCustomItem from './DropdownCustomItem';
import DropdownDivider from './DropdownDivider';
import DropdownFooter from './DropdownFooter';
import DropdownHeader from './DropdownHeader';
import DropdownItem from './DropdownItem';
import DropdownLevels from './DropdownLevels';
import DropdownNavigation from './DropdownNavigation';
import DropdownTitle from './DropdownTitle';
import { reasonsToToggle } from './constants';
import { StyledArrow, StyledContainerWithArrow } from './sharedStyles';
import debounce from 'lodash.debounce';

/**
 * Fallback for Node enviroments.
 */
global.ResizeObserver = global.ResizeObserver || class {
  observe() {}
  disconnect() {}
};
global.requestAnimationFrame = global.requestAnimationFrame || (callback => callback());
const ARROW_HEIGHT = '14px';
const ARROW_SHADOW_BLUR = '4px';
const DISPLAY_NAME = 'Dropdown';
const Root = styled.div.withConfig({
  displayName: "Dropdown__Root",
  componentId: "sc-rh9gsj-0"
})(["", " ", " display:none;position:relative;z-index:var(--zindex-1);will-change:transform;margin:0;border-radius:var(--border-radius-rounded);padding:var(--size-spacing-02) 0;min-width:160px;max-width:620px;background-color:var(--neutral-surface);box-shadow:var(--shadow-md);", " &:focus{outline:none;}", " &[data-popper-placement^='top'],&[data-popper-placement^='bottom']{", "{&:after{width:calc(100% + 2 * ", ");height:calc(50% + ", ");left:calc(-1 * ", ");}}}&[data-popper-placement^='right'],&[data-popper-placement^='left']{", "{&:after{width:calc(50% + ", ");height:calc(100% + 2 * ", ");top:calc(-1 * ", ");}}}&[data-popper-placement^='top']{", "{bottom:calc(", " / -2);&:after{bottom:50%;}}}&[data-popper-placement^='bottom']{", "{top:calc(", " / -2);&:after{top:50%;}}}&[data-popper-placement^='right']{", "{left:calc(", " / -2);&:after{left:50%;}}}&[data-popper-placement^='left']{", "{right:calc(", " / -2);&:after{right:50%;}}}"], ScopedCssBaseline, ScrollbarStyle, _ref => {
  let {
    $arrow
  } = _ref;
  return !$arrow && css(["overflow-y:auto;"]);
}, _ref2 => {
  let {
    $open
  } = _ref2;
  return $open && css(["display:flex;flex-direction:column;"]);
}, StyledArrow, ARROW_SHADOW_BLUR, ARROW_SHADOW_BLUR, ARROW_SHADOW_BLUR, StyledArrow, ARROW_SHADOW_BLUR, ARROW_SHADOW_BLUR, ARROW_SHADOW_BLUR, StyledArrow, ARROW_HEIGHT, StyledArrow, ARROW_HEIGHT, StyledArrow, ARROW_HEIGHT, StyledArrow, ARROW_HEIGHT);
const Dropdown = /*#__PURE__*/forwardRef((_ref3, ref) => {
  let {
      className,
      style: styleProp,
      id: idProp,
      arrow,
      anchorId,
      open: openProp,
      defaultOpen,
      position,
      keepMounted,
      offset: offsetProp,
      focused: focusedProp,
      unfocused,
      children,
      renderAnchor,
      anchorEl,
      popperOptions,
      onClickOutside,
      onEscape,
      onKeyDown,
      onToggle
    } = _ref3,
    rest = _objectWithoutPropertiesLoose(_ref3, _excluded);
  const [open, setOpen] = useControlled({
    value: openProp,
    defaultValue: defaultOpen
  });
  const [popperElement, setPopperElement] = useState(null);
  const rootRef = useMergeRefs([setPopperElement, ref]);
  const [anchor, setAnchor] = useState(anchorEl);
  const [arrowRef, setArrowRef] = useState(null);
  const [mounted, setMounted] = useState(false);
  const id = useId(idProp);
  const focused = focusedProp && !unfocused;
  const offset = parseInt(offsetProp);
  const {
    minHeight,
    maxHeight
  } = useDropdownContext();
  const {
    handleKeyboardNavigation,
    keyboardNavigationProps,
    clearActiveIndex
  } = useKeyboardNavigation(popperElement);
  useDeprecated({
    anchorId,
    originName: 'Dropdown',
    fallback: 'Dropdown from now on will use the trigger id automatic.'
  });
  useDeprecated({
    focused: !focusedProp || undefined,
    originName: 'Dropdown',
    fallback: 'Use `unfocused` property instead.'
  });
  const handleToggle = useCallback((event, reason) => {
    onToggle === null || onToggle === void 0 ? void 0 : onToggle(event, !open, {
      reason
    });
    setOpen(previous => !previous);
  }, [onToggle, open, setOpen]);
  const handleAnchorToggle = useCallback(event => handleToggle(event, reasonsToToggle.clickAnchor), [handleToggle]);
  const handleKeyDown = event => {
    if (!open) return;
    onKeyDown === null || onKeyDown === void 0 ? void 0 : onKeyDown(event);
    handleKeyboardNavigation(event);
    if (event.key === 'Escape') {
      handleToggle(event, reasonsToToggle.escape);
      onEscape === null || onEscape === void 0 ? void 0 : onEscape(event);
    }
  };
  useEventListener('mousedown', event => {
    if (!open) return;
    const contains = targetWithin(event.target, [popperElement, anchor]);
    if (!contains) {
      onClickOutside === null || onClickOutside === void 0 ? void 0 : onClickOutside(event);
      handleToggle(event, reasonsToToggle.clickOutside);
    }
  });
  const offsetModifier = useMemo(() => ({
    name: 'offset',
    phase: 'beforeMain',
    enabled: true,
    options: {
      offset: [0, offset + parseInt(arrow ? ARROW_HEIGHT : 0)]
    }
  }), [offset, arrow]);
  const focusModifier = useMemo(() => ({
    name: 'focus',
    phase: 'afterWrite',
    enabled: true,
    fn: _ref4 => {
      let {
        state
      } = _ref4;
      return focused && state.elements.popper.focus();
    }
  }), [focused]);
  const maxSizeModifier = useMemo(() => ({
    name: 'maxSize',
    enabled: true,
    phase: 'main',
    requiresIfExists: ['offset'],
    fn: _ref5 => {
      let {
        state,
        name,
        options
      } = _ref5;
      const overflow = detectOverflow(state, options);
      const {
        x,
        y
      } = state.modifiersData.preventOverflow || {
        x: 0,
        y: 0
      };
      const {
        width,
        height
      } = state.rects.popper;
      const [basePlacement] = state.placement.split('-');
      const widthProp = basePlacement === 'left' ? 'left' : 'right';
      const heightProp = basePlacement === 'top' ? 'top' : 'bottom';
      state.modifiersData[name] = {
        width: width - overflow[widthProp] - x,
        height: height - overflow[heightProp] - y
      };
    }
  }), []);
  const maxHeightModifier = useMemo(() => ({
    name: 'maxHeight',
    enabled: true,
    phase: 'beforeWrite',
    requires: ['maxSize'],
    fn(_ref6) {
      let {
        state
      } = _ref6;
      const {
        height
      } = state.modifiersData.maxSize;
      const heightIsMinorThanMinimum = height < minHeight;
      const heightIsLargeThanMaximum = height > maxHeight;
      state.styles.popper.maxHeight = "".concat(heightIsMinorThanMinimum ? minHeight : heightIsLargeThanMaximum ? maxHeight : height - parseInt(sizeSpacing04), "px");
    }
  }), [minHeight, maxHeight]);
  const resizeModifier = useMemo(() => ({
    name: 'resize',
    phase: 'afterWrite',
    enabled: true,
    effect: _ref7 => {
      let {
        instance,
        state
      } = _ref7;
      const observer = new global.ResizeObserver(debounce(() => global.requestAnimationFrame(() => instance.update()), 300));
      observer.observe(state.elements.reference);
      return () => observer.disconnect();
    }
  }), []);
  const arrowModifier = useMemo(() => ({
    name: 'arrow',
    phase: 'main',
    options: {
      element: arrowRef
    }
  }), [arrowRef]);
  const flipModifier = useMemo(() => ({
    name: 'flip',
    options: {
      fallbackPlacements: Object.values(positions)
    }
  }), []);
  const modifiers = [offsetModifier, focusModifier, maxSizeModifier, maxHeightModifier, resizeModifier, arrowModifier];
  if (arrow) modifiers.push(flipModifier);
  const {
    styles,
    attributes,
    update
  } = usePopper(anchor, popperElement, Object.assign({
    placement: position
  }, popperOptions, {
    modifiers
  }));
  const style = Object.assign({}, styles.popper, styleProp);
  useEffect(() => {
    anchorEl && setAnchor(anchorEl);
  }, [anchorEl]);
  useEffect(() => {
    if (keepMounted && !mounted) setMounted(true);
  }, [keepMounted, mounted]);
  useEffect(() => {
    if (keepMounted) {
      open && (anchor === null || anchor === void 0 ? void 0 : anchor.setAttribute('aria-owns', id));
    } else {
      open ? anchor === null || anchor === void 0 ? void 0 : anchor.setAttribute('aria-owns', id) : anchor === null || anchor === void 0 ? void 0 : anchor.removeAttribute('aria-owns');
    }
    anchor === null || anchor === void 0 ? void 0 : anchor.setAttribute('aria-expanded', !!open);
  }, [keepMounted, anchor, id, open]);
  useEffect(() => {
    open && (update === null || update === void 0 ? void 0 : update());
  }, [open, update]);
  useEffect(() => {
    !open && clearActiveIndex();
  }, [open, clearActiveIndex]);
  usePreventScroll(open);
  return /*#__PURE__*/React.createElement(DropdownInnerProvider, {
    toggle: handleToggle,
    keyboardNavigationProps: keyboardNavigationProps
  }, renderAnchor && /*#__PURE__*/React.createElement(DropdownAnchor, null, renderAnchor({
    anchorRef: setAnchor,
    toggle: handleAnchorToggle
  })), (keepMounted || open) && /*#__PURE__*/React.createElement(Root, _extends({}, rest, {
    id: id,
    className: className,
    style: style,
    ref: rootRef,
    tabIndex: open ? 0 : -1,
    onKeyDown: handleKeyDown,
    "aria-labelledby": anchorId || (anchor === null || anchor === void 0 ? void 0 : anchor.id),
    $open: open,
    $arrow: arrow
  }, attributes.popper), /*#__PURE__*/React.createElement(DefaultRegion, null, /*#__PURE__*/React.createElement(Boundary, {
    name: DISPLAY_NAME
  }, arrow ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(StyledArrow, {
    style: styles.arrow,
    ref: setArrowRef
  }), /*#__PURE__*/React.createElement(StyledContainerWithArrow, null, children)) : children))));
});
Dropdown.displayName = DISPLAY_NAME;
Dropdown.Style = Root;
Dropdown.ArrowStyle = StyledArrow;
Dropdown.Checkbox = DropdownCheckbox;
Dropdown.Container = DropdownContainer;
Dropdown.Header = DropdownHeader;
Dropdown.Footer = DropdownFooter;
Dropdown.CustomItem = DropdownCustomItem;
Dropdown.Item = DropdownItem;
Dropdown.Navigation = DropdownNavigation;
Dropdown.Levels = DropdownLevels;
Dropdown.Divider = DropdownDivider;
Dropdown.Title = DropdownTitle;
Dropdown.Provider = DropdownProvider;
Dropdown.Anchor = DropdownAnchor;
Dropdown.positions = {
  top: positions.top,
  topStart: positions.topStart,
  topEnd: positions.topEnd,
  bottom: positions.bottom,
  bottomStart: positions.bottomStart,
  bottomEnd: positions.bottomEnd,
  left: positions.left,
  leftStart: positions.leftStart,
  leftEnd: positions.leftEnd,
  right: positions.right,
  rightStart: positions.rightStart,
  rightEnd: positions.rightEnd
};
Dropdown.reasonsToToggle = reasonsToToggle;
Dropdown.defaultProps = {
  arrow: false,
  offset: sizeSpacing01,
  keepMounted: false,
  focused: true,
  unfocused: false,
  position: Dropdown.positions.bottomStart,
  popperOptions: {}
};
process.env.NODE_ENV !== "production" ? Dropdown.propTypes = {
  /**
   * Sets the children,
   * which usually contains `Dropdown.Container`, `Dropdown.Header` and `Dropdown.Footer`.
   */
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.func]).isRequired,
  /**
   * @ignore
   *
   * A boolean value to control if render with arrow.
   */
  arrow: PropTypes.bool,
  /**
   * Function responsible for rendering the `Dropdown` anchor.
   *
   * @param {React.Dispatch} anchorRef - Reference that must be passed to the `ref` property of the anchor component that will be rendered.
   * @param {Function} toggle - Function that opens and closes the `Dropdown` in the `uncontrolled` version.
   */
  renderAnchor: PropTypes.func,
  /**
   * Responsible for controlling if `Dropdown` will be rendered opened or closed in the uncontrolled version.
   */
  defaultOpen: PropTypes.bool,
  /**
   * Sets the anchor element (html element). Only used in the controlled version.
   */
  anchorEl: PropTypes.instanceOf(Element),
  /**
   * Controls whether `Dropdown` will be rendered opened in the controlled version.
   */
  open: PropTypes.bool,
  /**
   * Determines the position of `Dropdown` relative to the anchor element.
   *
   * **deprecated values:**
   * - `Dropdown.positions.top`,
   * - `Dropdown.positions.right`,
   * - `Dropdown.positions.bottom`,
   * - `Dropdown.positions.left`
   */
  position: PropTypes.oneOf([Dropdown.positions.top, Dropdown.positions.topStart, Dropdown.positions.topEnd, Dropdown.positions.right, Dropdown.positions.rightStart, Dropdown.positions.rightEnd, Dropdown.positions.left, Dropdown.positions.leftStart, Dropdown.positions.leftEnd, Dropdown.positions.bottom, Dropdown.positions.bottomStart, Dropdown.positions.bottomEnd]),
  /**
   * Sets the distance between the `Dropdown` and the anchor element.
   * Use as a number or a string with unit px.
   */
  offset: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  /**
   * Callback triggered when the `Dropdown` is open and outside of dropdown is clicked.
   *
   * @param {React.SyntheticEvent} event - The event source of the callback.
   */
  onClickOutside: PropTypes.func,
  /**
   * Callback triggered when the `Dropdown` is opened and ESC key is pressed.
   *
   * @param {React.SyntheticEvent} event - The event source of the callback.
   */
  onEscape: PropTypes.func,
  /**
   * Callback triggered when the `Dropdown` is focused and any key is pressed.
   *
   * @param {React.SyntheticEvent} event - The event source of the callback.
   */
  onKeyDown: PropTypes.func,
  /**
   * Callback triggered when the `Dropdown` requests to be closed or opened.
   *
   * @param {React.SyntheticEvent} event - The event source of the callback.
   * @param {boolean} open - `Dropdown` open state.
   * @param {object} infos - Additional information about component status.
   * @param {string} infos.reason - The reason to toggle the `Dropdown`. Can be `Dropdown.reasonsToToggle.escape`, `Dropdown.reasonsToToggle.clickOutside`, `Dropdown.reasonsToToggle.clickItem` or `Dropdown.reasonsToToggle.clickAnchor`.
   */
  onToggle: PropTypes.func,
  /**
   * @deprecated
   *
   * Options provided to the [`popper.js`](https://popper.js.org/docs/v1/) instance.
   *
   * @ignore
   */
  popperOptions: PropTypes.object,
  /**
   * Controls whether `Dropdown` will mount without focus.
   */
  unfocused: PropTypes.bool,
  /**
   * Sets if the `Dropdown` will be always mounted after the first mount. It can be useful for SEO.
   */
  keepMounted: PropTypes.bool,
  /**
   * @deprecated - `Dropdown` from now on will use the trigger id automatically.
   *
   * Sets the id used to connect accessibly `Dropdown` with anchor element.
   * The id value is used in `aria-labelledby`.
   */
  anchorId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /**
   * @deprecated - Use `unfocused` property instead.
   *
   * Sets if Dropdown will mount with focus or not.
   */
  focused: PropTypes.bool
} : void 0;
export default Dropdown;